import { cn } from "@simpro/ui";
import type { CSSProperties, ElementType, ReactNode } from "react";
import { STAGGER_MS } from "./presets";
import { useReveal } from "./useReveal";

export interface RevealProps {
  children: ReactNode;
  /** The element to render; a `li` inside a list, a `section` for a page section. */
  as?: ElementType;
  /** Its place among siblings that arrive together, for the stagger. */
  index?: number;
  className?: string;
  /** Passed through, for a section's `aria-labelledby` and the like. */
  [attribute: `aria-${string}` | `data-${string}`]: string | undefined;
  id?: string;
}

/**
 * Rises into place once it scrolls into view (`rise`, with `stagger` by `index`; design note
 * salon-booking-premium-redesign.md section 5.4). It is CSS, not the motion engine: the `reveal`
 * utility in app.css moves only opacity and transform, so a reveal never reflows the page.
 *
 * Content is never hidden for good. Without IntersectionObserver it is shown from the start; under
 * reduced motion the stylesheet shows it in place; and it is never used on anything a customer needs
 * at first sight, such as the hero's buttons.
 */
export function Reveal({ children, as: Tag = "div", index = 0, className, ...rest }: RevealProps) {
  const [ref, shown] = useReveal<HTMLElement>();
  const style = { "--reveal-delay": `${Math.min(index, 8) * STAGGER_MS}ms` } as CSSProperties;

  return (
    <Tag ref={ref} data-shown={shown} className={cn("reveal", className)} style={style} {...rest}>
      {children}
    </Tag>
  );
}
